import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Share, MoreVertical, X } from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "salesby:install-banner-dismissed-at";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const ENTRY_ROUTES = ["/", "/login", "/register"];

const isSnoozed = () => {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < SNOOZE_MS;
  } catch {
    return false;
  }
};

const InstallAppBanner = () => {
  const location = useLocation();
  const { isInstallable, isInstalled, platform, promptInstall } = usePWAInstall();
  const [dismissed, setDismissed] = useState(true);
  const [showSteps, setShowSteps] = useState(false);
  const [isPhone, setIsPhone] = useState(false);

  useEffect(() => {
    setDismissed(isSnoozed());
    const mql = window.matchMedia("(max-width: 767px)");
    const sync = () => setIsPhone(mql.matches);
    sync();
    mql.addEventListener("change", sync);
    return () => mql.removeEventListener("change", sync);
  }, []);

  if (isInstalled || dismissed || !isPhone) return null;
  if (!ENTRY_ROUTES.includes(location.pathname)) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch {}
  };

  const handleInstall = async () => {
    if (isInstallable) {
      const accepted = await promptInstall();
      if (accepted) return;
    }
    setShowSteps(true);
  };

  const steps =
    platform === "ios"
      ? [
          "Tap the Share button at the bottom of Safari",
          'Scroll down and tap "Add to Home Screen"',
          'Tap "Add" — Salesby appears as an app icon',
        ]
      : platform === "android"
        ? [
            "Tap the ⋮ menu at the top right of Chrome",
            'Tap "Install app" or "Add to Home screen"',
            'Confirm "Install" — Salesby appears in your apps',
          ]
        : [
            "Click the install icon in your browser address bar",
            'Or open the browser menu and choose "Install Salesby"',
          ];

  return (
    <div className="fixed inset-x-3 bottom-3 z-[70] md:hidden">
      <div className="rounded-2xl border border-border bg-card p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <img
            src="/pwa-icon-192.png"
            alt="Salesby"
            className="h-11 w-11 flex-shrink-0 rounded-xl"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">Install Salesby</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Add it to your home screen and open it like an app — faster, full screen, no
              browser bar.
            </p>
          </div>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="-mr-1 -mt-1 flex-shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent/20"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {showSteps ? (
          <ol className="mt-3 space-y-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
            {steps.map((step, i) => (
              <li key={step} className="flex gap-2">
                <span className="font-semibold text-foreground">{i + 1}.</span>
                <span className="flex items-center gap-1">
                  {step}
                  {i === 0 && platform === "ios" && <Share className="h-3.5 w-3.5" />}
                  {i === 0 && platform === "android" && <MoreVertical className="h-3.5 w-3.5" />}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <Button onClick={handleInstall} size="sm" className="mt-3 w-full">
            <Download className="mr-1.5 h-4 w-4" />
            {isInstallable ? "Install app" : "Show me how"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default InstallAppBanner;
